import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

type Period = {
  type: string;
  number: number;
  isRefund?: boolean;
  score?: string;
};

export type ResultLeague = {
  k: string | number;
  nn: Record<string, string>;
  m: {
    k: string | number;
    od: string;
    n: Record<string, { h: string; a: string }>;
    result?: {
      periods?: Period[];
      summary?: Record<string, string | undefined>;
    };
  }[];
};

type PeriodResultTableProps = {
  leagues: ResultLeague[];
  periodColumns: readonly { label: string; type: string; number: number }[];
  summaryColumns: readonly { label: string; key: string }[];
  combineScores?: boolean;
};

const teamScore = (score: string | undefined, teamIndex: number) =>
  score?.split(":")[teamIndex]?.trim() || "-";

const scorePair = (score: string | undefined) => score?.replace(/\s+/g, "") || "-";

const PeriodResultTable = ({
  leagues,
  periodColumns,
  summaryColumns,
  combineScores = false,
}: PeriodResultTableProps) => {
  const { i18n, t } = useTranslation();
  const headers = [
    t("Date & Time"),
    t("Competition"),
    ...periodColumns.map((column) => t(column.label)),
    ...summaryColumns.map((column) => t(column.label)),
  ];
  const leaguesWithMatches = leagues.filter((league) => league.m.length > 0);

  return (
    <div className="period-results-scroll">
      <table className="period-results w-full">
        <thead>
          <tr>
            {headers.map((header) => <th key={header} scope="col">{header}</th>)}
          </tr>
        </thead>
        {leaguesWithMatches.length === 0 && (
          <tbody>
            <tr>
              <td colSpan={headers.length} className="text-center">{t("No competition")}</td>
            </tr>
          </tbody>
        )}
        {leaguesWithMatches.map((league) => (
          <tbody key={league.k}>
            <tr>
              <th className="league text-left" scope="rowgroup" colSpan={headers.length}>
                {league.nn[i18n.language] ?? league.nn.en}
              </th>
            </tr>
            {league.m.map((match, matchIndex) => {
              const teams = match.n[i18n.language] ?? match.n.en;
              const periods = match.result?.periods ?? [];
              const rowClass = matchIndex % 2 === 0 ? "odd-row" : "even-row";

              return (
                <Fragment key={match.k}>
                  {(["h", "a"] as const).map((team, teamIndex) => {
                    const shouldRenderScores = !combineScores || teamIndex === 0;

                    return (
                      <tr key={team} className={rowClass}>
                        {teamIndex === 0 && (
                          <td className="period-date" rowSpan={2}>
                            {moment(match.od).format("DD-MM-YYYY HH:mm")}
                          </td>
                        )}
                        <td className="period-team">{teams[team]}</td>
                        {shouldRenderScores && (
                          <>
                            {periodColumns.map((column) => {
                              const period = periods.find(
                                (item) => item.type === column.type && item.number === column.number
                              );
                              const score = combineScores
                                ? scorePair(period?.score)
                                : teamScore(period?.score, teamIndex);

                              return (
                                <td key={column.label} className="period-score" rowSpan={combineScores ? 2 : undefined}>
                                  {score}
                                </td>
                              );
                            })}
                            {summaryColumns.map((column) => (
                              <td key={column.key} className="period-score" rowSpan={combineScores ? 2 : undefined}>
                                {combineScores
                                  ? scorePair(match.result?.summary?.[column.key])
                                  : teamScore(match.result?.summary?.[column.key], teamIndex)}
                              </td>
                            ))}
                          </>
                        )}
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
};

export default PeriodResultTable;
